import React, { createContext, useContext, useEffect, useState } from "react";
import { ThemeProvider as GenerativeUIThemeProvider } from "@openuidev/react-ui/ThemeProvider";
import type { Theme as GenerativeUITheme } from "@openuidev/react-ui/ThemeProvider";
import { background, border } from "@/config/theme";
import { environment } from '@/config/posthog.config';
import { POSTHOG } from "@/constants";
import { usePostHogTracking } from "@/hooks/usePostHogTracking";

type Theme = "dark" | "light";

type ThemeProviderProps = {
  children: React.ReactNode;
  defaultTheme?: Theme;
  storageKey?: string;
  enableAnimation?: boolean;
  fontConfig?: Partial<FontConfig>;
};

type ThemeProviderState = {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggleTheme: (coords?: { x: number; y: number }) => void;
};

// Font configuration type based on the generative-ui ThemeProvider
type FontConfig = {
  fontLabel?: string;
  fontLabelLetterSpacing?: string;
  fontLabelHeavy?: string;
  fontLabelHeavyLetterSpacing?: string;
  fontLabelSmall?: string;
  fontLabelSmallLetterSpacing?: string;
  fontLabelSmallHeavy?: string;
  fontLabelSmallHeavyLetterSpacing?: string;
  fontLabelExtraSmall?: string;
  fontLabelExtraSmallLetterSpacing?: string;
  fontLabelExtraSmallHeavy?: string;
  fontLabelExtraSmallHeavyLetterSpacing?: string;
  fontLabelLargeHeavy?: string;
  fontLabelLargeHeavyLetterSpacing?: string;
  fontLabelLarge?: string;
  fontLabelLargeLetterSpacing?: string;
  fontLabelMediumHeavy?: string;
  fontLabelMediumHeavyLetterSpacing?: string;
  fontLabelMedium?: string;
  fontLabelMediumLetterSpacing?: string;
};

const initialState: ThemeProviderState = {
  theme: "light",
  setTheme: () => null,
  toggleTheme: () => null,
};

const ThemeProviderContext = createContext<ThemeProviderState>(initialState);

export function ThemeProvider({
  children,
  defaultTheme = "light",
  storageKey = "sarasiq-theme",
  enableAnimation = false,
  fontConfig = {},
  ...props
}: ThemeProviderProps) {
  // Priority: stored preference > system preference > default. A returning user's
  // explicit choice (persisted in `storageKey`) must win, so toggling to light and
  // reloading keeps light — even on a machine whose OS prefers dark. The OS
  // `prefers-color-scheme` is only a first-visit default, used when nothing is
  // stored yet.
  const getInitialTheme = (): Theme => {
    // Check if window is defined (client-side)
    if (typeof window !== 'undefined') {
      // 1. Stored preference wins for a returning user.
      const storedTheme = localStorage.getItem(storageKey);
      if (storedTheme === 'light' || storedTheme === 'dark') return storedTheme;

      // 2. First visit (nothing stored): honour the OS preference.
      if (window.matchMedia('(prefers-color-scheme: dark)').matches) return 'dark';
    }

    // 3. Fall back to the explicit default.
    return defaultTheme;
  };

  const [theme, setTheme] = useState<Theme>(getInitialTheme);
  const { trackEvent } = usePostHogTracking();

  useEffect(() => {
    const root = window.document.documentElement;
    
    // Remove the old class
    root.classList.remove("light", "dark");
    
    // Add the new class
    root.classList.add(theme);
  }, [theme]);
  
  // Listen for system preference changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleChange = () => {
      // Only update if user hasn't manually set a preference
      const hasUserPreference = localStorage.getItem(storageKey) !== null;
      if (!hasUserPreference) {
        setTheme(mediaQuery.matches ? 'dark' : 'light');
      }
    };
    
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [storageKey]);

  useEffect(() => {
    // Update localStorage when theme changes
    localStorage.setItem(storageKey, theme);
  }, [theme, storageKey]);

  // Add or remove the animation class based on the enableAnimation prop
  useEffect(() => {
    const root = window.document.documentElement;
    
    if (enableAnimation) {
      root.classList.add("theme-animation-enabled");
    } else {
      root.classList.remove("theme-animation-enabled");
    }
    
    return () => {
      root.classList.remove("theme-animation-enabled");
    };
  }, [enableAnimation]);

  const handleThemeChange = (newTheme: Theme) => {
    const previousTheme = theme;
    setTheme(newTheme);
    trackEvent(POSTHOG.EVENTS.THEME_CHANGED, {
      from_theme: previousTheme,
      to_theme: newTheme,
      theme: newTheme,
      environment,
    });
  };

  const handleThemeToggle = (coords?: { x: number; y: number }) => {
    const root = document.documentElement;
    const newTheme = theme === "light" ? "dark" : "light";

    // Check if the user prefers reduced motion
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    // Check if animation is enabled and browser supports View Transitions API
    // and user doesn't prefer reduced motion
    if (!enableAnimation || !document.startViewTransition || prefersReducedMotion) {
      handleThemeChange(newTheme);
      return;
    }

    // Set the animation origin point if coordinates are provided
    if (coords) {
      // Convert pixel coordinates to percentages for more reliable positioning
      const xPercent = (coords.x / window.innerWidth) * 100;
      const yPercent = (coords.y / window.innerHeight) * 100;
      
      
      // Set the CSS variables as percentages
      root.style.setProperty("--x-percent", `${xPercent}%`);
      root.style.setProperty("--y-percent", `${yPercent}%`);
      
      // Force a reflow to ensure the CSS variables are applied before the animation starts
      void root.offsetWidth;
    } else {
      // Default to center of viewport if no coordinates provided
      root.style.setProperty("--x-percent", `50%`);
      root.style.setProperty("--y-percent", `50%`);
    }

    // Start the view transition
    document.startViewTransition(() => {
      handleThemeChange(newTheme);
    });
  };

  const value = {
    theme,
    setTheme: handleThemeChange,
    toggleTheme: handleThemeToggle,
  };

  // Create theme objects for generative-ui ThemeProvider
  const lightTheme: Partial<GenerativeUITheme> = {
    // Add font properties from fontConfig
    ...fontConfig,
    ...background.light,
    ...border.light,
  };

  const darkTheme: Partial<GenerativeUITheme> = {
    // Add font properties from fontConfig
    ...fontConfig,
    ...background.dark,
    ...border.dark,
  };

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      <GenerativeUIThemeProvider 
        mode={theme} 
        theme={lightTheme} 
        darkTheme={darkTheme}
      >
        {children}
      </GenerativeUIThemeProvider>
    </ThemeProviderContext.Provider>
  );
}

export const useTheme = () => {
  const context = useContext(ThemeProviderContext);

  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }

  return context;
};

// Helper function to create font configuration
export const createFontConfig = (config: Partial<FontConfig>): Partial<FontConfig> => {
  return config;
};